import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Entity } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { useMapState, useMapUtils } from '@shapeable/maps';
import { CellLabel, IconToggleButton, SidebarButton, useLang } from '@shapeable/ui';
import { DcoWorldIconGlyph } from '../icons/dco-world-icon';
import { DcoOrganisationsIcon, DcoOrganisationsIconGlyph } from '../icons/dco-organisation-icon';
import { DcoTownTeamsIcon, DcoTownTeamsIconGlyph } from '../icons/dco-town-teams-icon';
import { DcoDoingsIcon, DcoDoingsIconGlyph } from '../icons/dco-doings-icon';

const cls = classNames('sidebar-buttons');

// -------- Types -------->

// -------- Props -------->

export type SidebarButtonsProps = Classable & HasChildren & { 
  entity?: Entity;
};

export const SidebarButtonsDefaultProps: Omit<SidebarButtonsProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
  
  `,
});

const TogglesWrapperStyles = breakpoints({
  base: css`
    ${theme.H_DOT_BG('light', 2, 6)};
    padding-top: ${theme.UNIT(4)};
    display: flex;
    align-items: center;
    flex-direction: column;
  `,
});

const ToggleStyles = breakpoints({
  base: css`
    cursor: pointer;
    margin-top: ${theme.UNIT(1)};
    margin-bottom: ${theme.UNIT(3)};
    font-family: ${theme.FONT('sans')};
    font-size: ${theme.FONT_SIZE(10)};
    width: 100%;
    text-align: center;
    ${theme.TRANSITION('scale')};

    &:hover {
      scale: 1.05;
    }

    svg {
      width: 28px;
      height: 28px;
    }
  `,
});

const ToggleLabelStyles = breakpoints({
  base: css`
    font-weight: 500;
    font-size: ${theme.FONT_SIZE(10)};
    margin-bottom: ${theme.UNIT(2)};
    text-align: center;
  `,
});

const WorldMapStyles = breakpoints({
  base: css`
   
  `,
});


const WorldMapIconStyles = breakpoints({
  base: css`
    width: 40px;
    height: 40px;
    display: none;
    margin-bottom: ${theme.UNIT(3)};
  `,
  tablet: css`
    display: flex;
  `
});


// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,

  WorldMap: styled(SidebarButton)`${WorldMapStyles}`,
    WorldMapIcon: styled(DcoWorldIconGlyph)`${WorldMapIconStyles}`,
  
  TogglesWrapper: styled.div`${TogglesWrapperStyles}`,
    ToggleLabel: styled(CellLabel)`${ToggleLabelStyles}`,
    Toggle: styled(IconToggleButton)`${ToggleStyles}`,
};

export const SidebarButtons: Shapeable.FC<SidebarButtonsProps> = (props) => {
  const { className, children, entity } = props;

  const t = useLang();
  const map = useMapUtils();
  const [ mapState ] = useMapState();
  const { toggleEntityType, entityTypeIsVisible } = useMapUtils();
  
  return (
    <My.Container className={cls.name(className)}>
      {
        mapState.isVisible &&
        <My.TogglesWrapper>
          <My.ToggleLabel>{t('Show:')}</My.ToggleLabel>
          <My.Toggle onClick={() => { toggleEntityType('Organisation') }} isActive={entityTypeIsVisible('Organisation')} icon={<DcoOrganisationsIconGlyph />}>{t('All Orgs')}</My.Toggle>
          <My.Toggle onClick={() => { toggleEntityType('Space') }} isActive={entityTypeIsVisible('Space')} icon={<DcoTownTeamsIconGlyph />}>{t('All Doers')}</My.Toggle>
          <My.Toggle onClick={() => { toggleEntityType('ImpactStory') }} isActive={entityTypeIsVisible('ImpactStory')} icon={<DcoDoingsIconGlyph />}>{t('All Doings')}</My.Toggle>
        </My.TogglesWrapper>
      }
      {
        !map.isVisible && 
        <My.WorldMap onClick={map.showOnClick} icon={<My.WorldMapIcon />}>{t('World Map')}</My.WorldMap>
      }
    </My.Container>
  )
};

SidebarButtons.cls = cls;